import axios from 'axios';

// URL del backend - usar URLs relativas en producción para evitar problemas de CORS
// En producción (cuando el host no es localhost/preview), siempre usar URLs relativas
const isLocalDev = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || 
   window.location.hostname.includes('preview.emergentagent.com'));
const BACKEND_URL = isLocalDev ? (process.env.REACT_APP_BACKEND_URL || '') : '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  changePassword: (oldPassword, newPassword) => 
    api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
  verifyToken: () => api.post('/auth/verify-token'),
  setPreferenciasPush: (noMostrar) => api.post('/auth/preferencias-push', { no_mostrar_modal_push: noMostrar }),
};

// Talleres
export const talleresAPI = {
  getAll: (params) => api.get('/talleres/', { params }),
  getPublicos: (params) => api.get('/talleres/publicos', { params }),
  getOne: (id) => api.get(`/talleres/${id}`),
  create: (data) => api.post('/talleres/', data),
  update: (id, data) => api.put(`/talleres/${id}`, data),
  delete: (id) => api.delete(`/talleres/${id}`),
  getConfig: () => api.get('/talleres/config'),
  getUsuarios: (id) => api.get(`/talleres/${id}/usuarios`),
  updateCapacidades: (id, capacidades) => api.post(`/talleres/${id}/capacidades`, capacidades),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/talleres/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Búsqueda de talleres sin suscripción (públicos - para formulario de suscripción)
  buscarSinSuscripcion: (query) => api.get('/talleres/buscar-sin-suscripcion', { params: { q: query } }),
  // Verificar si un número ANAC existe (público - para validación en formulario)
  verificarAnac: (anac) => api.get('/talleres/verificar-anac', { params: { anac } }),
};

// Fabricantes
export const fabricantesAPI = {
  getAll: (params) => api.get('/fabricantes/', { params }),
  getRubros: () => api.get('/fabricantes/rubros'),
  getOne: (id) => api.get(`/fabricantes/${id}`),
  create: (data) => api.post('/fabricantes/', data),
  update: (id, data) => api.put(`/fabricantes/${id}`, data),
  delete: (id) => api.delete(`/fabricantes/${id}`),
  getModelos: (fabricanteId) => api.get(`/fabricantes/${fabricanteId}/modelos`),
  createModelo: (fabricanteId, data) => api.post(`/fabricantes/${fabricanteId}/modelos`, data),
  deleteModelo: (modeloId) => api.delete(`/fabricantes/modelos/${modeloId}`),
  getAllModelos: () => api.get('/fabricantes/modelos/todos'),
};

// Documentos
export const documentosAPI = {
  getManuales: (params) => api.get('/documentos/manuales', { params }),
  getManual: (id) => api.get(`/documentos/manuales/${id}`),
  createManual: (data) => api.post('/documentos/manuales', data),
  updateManual: (id, data, pdf_confirmado = false, deferNotification = false) => 
    api.put(`/documentos/manuales/${id}?notificar=true&pdf_confirmado=${pdf_confirmado}&defer_notification=${deferNotification}`, data),
  deleteManual: (id) => api.delete(`/documentos/manuales/${id}`),
  uploadManualPdf: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documentos/manuales/${id}/pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 minutos para archivos grandes
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
  },
  
  // Dashboard de estado de PDFs
  getPdfDashboard: () => api.get('/documentos/dashboard/pdf-status'),
  getDocumentosSinPdf: (fabricanteId, page = 1, limit = 50) => {
    const params = { page, limit };
    if (fabricanteId) params.fabricante_id = fabricanteId;
    return api.get('/documentos/dashboard/documentos-sin-pdf', { params });
  },
  
  // Documentos incompletos (con cualquier faltante)
  getDocumentosIncompletos: (fabricanteId, tipoFaltante = 'todos', page = 1, limit = 50) => {
    const params = { page, limit, tipo_faltante: tipoFaltante };
    if (fabricanteId) params.fabricante_id = fabricanteId;
    return api.get('/documentos/dashboard/documentos-incompletos', { params });
  },
  
  // Subida por chunks para archivos muy grandes (> 30MB)
  initChunkedUpload: (manualId, filename, filesize, totalChunks) => {
    const formData = new FormData();
    formData.append('filename', String(filename));
    formData.append('filesize', String(filesize));
    formData.append('total_chunks', String(totalChunks));
    return api.post(`/documentos/manuales/${manualId}/pdf/init-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  uploadChunk: (manualId, uploadId, chunkIndex, chunk) => {
    const formData = new FormData();
    formData.append('upload_id', String(uploadId));
    formData.append('chunk_index', String(chunkIndex));
    formData.append('chunk', chunk, `chunk_${chunkIndex}`);
    return api.post(`/documentos/manuales/${manualId}/pdf/upload-chunk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000, // 3 minutos por chunk para conexiones lentas
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
  },
  
  completeChunkedUpload: (manualId, uploadId) => {
    const formData = new FormData();
    formData.append('upload_id', String(uploadId));
    return api.post(`/documentos/manuales/${manualId}/pdf/complete-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 minutos para ensamblar archivo grande en GridFS
    });
  },
  
  // Cloudflare R2 - Subida directa para archivos muy grandes (750MB+)
  getR2Status: () => api.get('/documentos/r2/status'),
  
  getR2PresignedUrl: (manualId, formData) => {
    return api.post(`/documentos/manuales/${manualId}/r2/presigned-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  confirmR2Upload: (manualId, formData, deferNotification = false) => {
    return api.post(`/documentos/manuales/${manualId}/r2/confirm-upload?defer_notification=${deferNotification}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Precacheo individual del documento (LOW -> HD -> OCR).
  // Se llama despues de confirmR2Upload cuando el admin tildo la opcion.
  precachearIndividual: (manualId, forceLarge = false) => {
    const formData = new FormData();
    formData.append('force_large', forceLarge ? 'true' : 'false');
    return api.post(`/documentos/admin/cache/precachear-individual/${manualId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getEstadoPrecacheIndividual: () => api.get('/documentos/admin/cache/estado-individual'),
  // Progreso del precacheo para UN documento (usado por el badge en el card admin y en el visor)
  getPrecacheProgressDoc: (manualId) => api.get(`/documentos/admin/cache/progress-doc/${manualId}`),
  cancelarPrecacheIndividual: (manualId) =>
    api.post(`/documentos/admin/cache/cancelar-individual/${manualId}`),

  // Proxy upload: sube el PDF a través del backend (evita CORS con R2)
  proxyUploadPdf: (manualId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documentos/manuales/${manualId}/r2/proxy-upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 minutos para PDFs grandes
      onUploadProgress: onProgress,
    });
  },

  getPublicaciones: (params) => api.get('/documentos/publicaciones', { params }),
  createPublicacion: (data) => api.post('/documentos/publicaciones', data),
  getDirectivas: (params) => api.get('/documentos/directivas', { params }),
  createDirectiva: (data) => api.post('/documentos/directivas', data),
  getTipos: () => api.get('/documentos/tipos'),
  createTipo: (data) => api.post('/documentos/tipos', data),
  getPrecioModeloCompleto: (modeloId) => api.get(`/documentos/modelos/${modeloId}/precio-completo`),
  
  // === TOC (Tabla de Contenidos) ===
  // Estadísticas del TOC de todos los documentos
  getEstadisticasToc: () => api.get('/documentos/admin/estadisticas-toc'),
  
  // Extraer TOC en batch (más seguro, menos documentos por vez)
  extraerTocBatch: (limit = 10, soloEnCache = false) => 
    api.post(`/documentos/admin/extraer-toc-batch?limit=${limit}&solo_en_cache=${soloEnCache}`, {}, {
      timeout: 120000, // 2 minutos máximo para evitar que Cloudflare corte
    }),
  
  // Obtener lista de documentos sin índice (escaneados)
  getDocumentosSinIndice: (page = 1, limit = 50) =>
    api.get(`/documentos/admin/documentos-sin-indice?page=${page}&limit=${limit}`),
  
  // Subir TOC manual para un documento
  subirTocManual: (manualId, tocData) =>
    api.post(`/documentos/admin/manuales/${manualId}/toc-manual`, tocData),
  eliminarTocManual: (manualId) =>
    api.delete(`/documentos/admin/manuales/${manualId}/toc-manual`),
  
  // Obtener documentos pendientes de procesar (todos)
  getDocumentosPendientesToc: () =>
    api.get('/documentos/admin/documentos-pendientes-toc'),
  
  // Marcar un documento como "sin índice" manualmente (para PDFs muy grandes)
  marcarSinIndice: (manualId) =>
    api.post(`/documentos/admin/manuales/${manualId}/marcar-sin-indice`),
  
  // Limpieza masiva de cache de páginas renderizadas
  limpiarCacheMasivo: () =>
    api.post('/documentos/admin/cache/limpiar-masivo', {}, { timeout: 30000 }),
  
  // Estado de limpieza masiva
  estadoLimpiezaMasiva: () =>
    api.get('/documentos/admin/cache/estado-limpieza'),
  
  // Pre-cacheo masivo de páginas
  precachearTodo: () =>
    api.post('/documentos/admin/cache/precachear-todo', {}, { timeout: 30000 }),
  
  // Estado de pre-cacheo masivo
  estadoPrecacheo: () =>
    api.get('/documentos/admin/cache/estado-precacheo'),
  
  // Cancelar pre-cacheo masivo
  cancelarPrecacheo: () =>
    api.post('/documentos/admin/cache/cancelar-precacheo'),
  
  // Limpiar progreso guardado (empezar desde cero)
  limpiarProgresoPrecacheo: () =>
    api.post('/documentos/admin/cache/limpiar-progreso-precacheo'),
  
  // Pre-cachear solo PDFs grandes (modo conservador)
  precachearGrandes: () =>
    api.post('/documentos/admin/cache/precachear-grandes'),

  precachearHD: () =>
    api.post('/documentos/admin/cache/precachear-hd', {}, { timeout: 30000 }),

  estadoPrecacheoHD: () =>
    api.get('/documentos/admin/cache/estado-precacheo-hd'),

  cancelarPrecacheoHD: () =>
    api.post('/documentos/admin/cache/cancelar-precacheo-hd'),

  limpiarProgresoPrecacheoHD: () =>
    api.post('/documentos/admin/cache/limpiar-progreso-precacheo-hd'),

  // OCR Batch
  procesarOCR: () =>
    api.post('/documentos/admin/ocr/procesar-escaneados', {}, { timeout: 30000 }),

  estadoOCR: () =>
    api.get('/documentos/admin/ocr/estado'),

  cancelarOCR: () =>
    api.post('/documentos/admin/ocr/cancelar'),

  resetearOCR: () =>
    api.post('/documentos/admin/ocr/resetear'),
};

// Compras
export const comprasAPI = {
  getCarrito: () => api.get('/compras/carrito'),
  agregarAlCarrito: (item) => api.post('/compras/carrito/agregar', item),
  quitarDelCarrito: (tipo, documentoId, modeloId) => 
    api.delete('/compras/carrito/item', { params: { tipo, documento_id: documentoId, modelo_id: modeloId } }),
  vaciarCarrito: () => api.delete('/compras/carrito'),
  crearSolicitudCompra: (notas) => api.post('/compras/solicitud', null, { params: { notas } }),
  getSolicitudes: (params) => api.get('/compras/solicitudes', { params }),
  aprobarSolicitud: (id) => api.post(`/compras/solicitudes/${id}/aprobar`),
  rechazarSolicitud: (id, motivo) => api.post(`/compras/solicitudes/${id}/rechazar`, null, { params: { motivo } }),
  solicitarManual: (data) => api.post('/compras/solicitar-manual', data),
  getSolicitudesManual: (params) => api.get('/compras/solicitudes-manual', { params }),
  aprobarSolicitudManual: (id) => api.post(`/compras/solicitudes-manual/${id}/aprobar`),
};

// Usuarios
export const usuariosAPI = {
  getAll: (params) => api.get('/usuarios/', { params }),
  getOne: (id) => api.get(`/usuarios/${id}`),
  getByTaller: (tallerId) => api.get(`/usuarios/taller/${tallerId}`),
  create: (data) => api.post('/usuarios/', data),
  update: (id, data) => api.put(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`),
  resetPassword: (id, nuevaPassword) => 
    api.post(`/usuarios/reset-password/${id}`, null, { params: { nueva_password: nuevaPassword } }),
  initAdmins: () => api.post('/usuarios/init-admins'),
};

// Finanzas
export const finanzasAPI = {
  getMovimientos: (params) => api.get('/finanzas/movimientos', { params }),
  getResumen: (params) => {
    if (typeof params === 'string') {
      // Soporte para periodo simple o "custom&fecha_desde=...&fecha_hasta=..."
      if (params.includes('&')) {
        const parts = params.split('&');
        const queryParams = {};
        parts.forEach(p => {
          if (p.includes('=')) {
            const [k, v] = p.split('=');
            queryParams[k] = v;
          } else {
            queryParams.periodo = p;
          }
        });
        return api.get('/finanzas/resumen', { params: queryParams });
      }
      return api.get('/finanzas/resumen', { params: { periodo: params } });
    }
    return api.get('/finanzas/resumen', { params });
  },
  registrarIngreso: (data) => api.post('/finanzas/ingresos', data),
  registrarPagoSuscripcion: (data) => api.post('/finanzas/ingresos/suscripcion', data),
  getTalleresSuscripcion: (tipo = 'renovacion') => api.get(`/finanzas/talleres-suscripcion?tipo=${tipo}`),
  migrarPagosManuales: () => api.post('/finanzas/migrar-pagos-manuales'),
  registrarEgreso: (data) => api.post('/finanzas/egresos', data),
  deleteMovimiento: (id) => api.delete(`/finanzas/movimientos/${id}`),
  getCategorias: () => api.get('/finanzas/categorias'),
  uploadComprobante: (formData) => api.post('/finanzas/upload-comprobante', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Nuevas APIs
  getMetricas: () => api.get('/finanzas/metricas'),
  registrarCompraDivisa: (data) => api.post('/finanzas/compra-divisa', data),
  getComprasDivisas: (params) => api.get('/finanzas/compras-divisas', { params }),
  exportarExcel: (params) => api.get('/finanzas/exportar-excel', {
    params: typeof params === 'string' ? { periodo: params } : (params || {}),
    responseType: 'blob'
  }),
  // Pagos de suscripciones
  getPagosSuscripciones: (params) => api.get('/finanzas/pagos-suscripciones', { params }),
};

// API de Suscripciones (admin)
export const suscripcionesAPI = {
  getDashboard: () => api.get('/suscripciones/dashboard'),
  getPorVencer: (dias = 7) => api.get('/suscripciones/por-vencer', { params: { dias } }),
  getEnGracia: (limit = 100) => api.get('/suscripciones/en-gracia', { params: { limit } }),
  getSuspendidos: (limit = 50) => api.get('/suscripciones/suspendidos', { params: { limit } }),
  ejecutarCron: () => api.post('/suscripciones/ejecutar-cron'),
  reactivarTaller: (tallerId, nivel, dias = 30) => 
    api.post(`/suscripciones/reactivar/${tallerId}`, null, { params: { nivel, dias } }),
  getHistorialCron: (limit = 20) => api.get('/suscripciones/historial-cron', { params: { limit } }),
};

// Notificaciones Masivas (Centro de comunicación)
export const notificacionesMasivasAPI = {
  preview: (payload) => api.post('/notificaciones-masivas/preview', payload),
  enviar: (payload) => api.post('/notificaciones-masivas/enviar', payload),
  getHistorial: (limit = 20) => api.get('/notificaciones-masivas/historial', { params: { limit } }),
  getDetalle: (id) => api.get(`/notificaciones-masivas/historial/${id}`),
  getExcluidos: () => api.get('/notificaciones-masivas/excluidos'),
  toggleExclusion: (tallerId, excluir) =>
    api.patch(`/notificaciones-masivas/talleres/${tallerId}/exclusion`, null, { params: { excluir } }),
};

// Ranking de Clientes (Fase C)
export const rankingClientesAPI = {
  getRanking: (params) => api.get('/suscripciones/ranking', { params }),
  exportCSV: (params) =>
    api.get('/suscripciones/ranking/export-csv', { params, responseType: 'blob' }),
};

// Solicitudes
export const solicitudesAPI = {
  getSuscripcion: (params) => api.get('/solicitudes/suscripcion', { params }),
  crearSuscripcion: (data) => api.post('/solicitudes/suscripcion', data),
  aprobarSuscripcion: (id) => api.post(`/solicitudes/suscripcion/${id}/aprobar`),
  rechazarSuscripcion: (id, motivo, observaciones) => 
    api.post(`/solicitudes/suscripcion/${id}/rechazar`, null, { params: { motivo, observaciones } }),
  solicitarUsuariosExtra: (cantidad, notas) => 
    api.post('/solicitudes/usuarios-extra', null, { params: { cantidad, notas } }),
  getUsuariosExtra: (params) => api.get('/solicitudes/usuarios-extra', { params }),
  aprobarUsuariosExtra: (id) => api.post(`/solicitudes/usuarios-extra/${id}/aprobar`),
  // Capacidades temporales
  solicitarCapacidades: (fabricante_id, modelos_ids, notas) => 
    api.post('/solicitudes/capacidades', null, { params: { fabricante_id, modelos_ids: modelos_ids.join(','), notas } }),
  getCapacidades: (params) => api.get('/solicitudes/capacidades', { params }),
  getCapacidadById: (id) => api.get(`/solicitudes/capacidades/${id}`),
  aprobarCapacidades: (id) => api.post(`/solicitudes/capacidades/${id}/aprobar`),
  rechazarCapacidades: (id, motivo) => api.post(`/solicitudes/capacidades/${id}/rechazar`, null, { params: { motivo } }),
  // Talleres Itinerantes
  getTalleresItinerantes: (params) => api.get('/solicitudes/talleres-itinerantes', { params }),
  actualizarEstadoTallerItinerante: (id, estado) => api.patch(`/solicitudes/talleres-itinerantes/${id}/estado`, null, { params: { estado } }),
};

// Notificaciones
export const notificacionesAPI = {
  getAll: (soloNoLeidas = false, limit = 50) => 
    api.get('/notificaciones/', { params: { solo_no_leidas: soloNoLeidas, limit } }),
  getCount: () => api.get('/notificaciones/count'),
  marcarLeida: (id) => api.post(`/notificaciones/${id}/leer`),
  marcarTodasLeidas: () => api.post('/notificaciones/leer-todas'),
  marcarMultiplesLeidas: (ids) => api.post('/notificaciones/leer-multiples', { ids }),
  eliminar: (id) => api.delete(`/notificaciones/${id}`),
  eliminarMultiples: (ids) => api.post('/notificaciones/eliminar-multiples', { ids }),
  eliminarTodas: () => api.delete('/notificaciones/'),
  crear: (data) => api.post('/notificaciones/', data),
  broadcast: (titulo, mensaje, tipo, link, soloPremium) => 
    api.post('/notificaciones/broadcast', null, { params: { titulo, mensaje, tipo, link, solo_premium: soloPremium } }),
  getAuditoria: (params) => api.get('/notificaciones/auditoria', { params }),
};

// Cursos
export const cursosAPI = {
  getAll: (params) => api.get('/cursos/', { params }),
  getAdmin: (params) => api.get('/cursos/admin', { params }),
  getOne: (id) => api.get(`/cursos/${id}`),
  create: (data) => api.post('/cursos/', data),
  update: (id, data) => api.put(`/cursos/${id}`, data),
  delete: (id) => api.delete(`/cursos/${id}`),
  inscribir: (cursoId, fechaIndex, notas) => 
    api.post(`/cursos/${cursoId}/inscribir`, null, { params: { fecha_index: fechaIndex, notas } }),
  getInscripciones: (cursoId) => api.get(`/cursos/${cursoId}/inscripciones`),
  // Inscripción pública (sin login)
  inscripcionPublica: (data) => api.post('/cursos/inscripcion-publica', data),
  getTodasInscripciones: (params) => api.get('/cursos/inscripciones/todas', { params }),
  updateEstadoInscripcion: (id, estado) => api.put(`/cursos/inscripciones/${id}/estado`, null, { params: { nuevo_estado: estado } }),
};

// Bajas / Cancelaciones
export const bajasAPI = {
  crear: (data) => api.post('/bajas', data),
  getAll: (params) => api.get('/bajas', { params }),
  getMiEstado: () => api.get('/bajas/mi-estado'),
  aceptar: (id) => api.post(`/bajas/${id}/aceptar`),
  rechazar: (id, nota) => api.post(`/bajas/${id}/rechazar`, null, { params: { nota } }),
};

// Descargas (registro y estadísticas)
export const descargasAPI = {
  registrar: (documentoId, documentoNombre, documentoTipo) => 
    api.post('/descargas/registrar', null, { 
      params: { documento_id: documentoId, documento_nombre: documentoNombre, documento_tipo: documentoTipo } 
    }),
  getEstadisticas: () => api.get('/descargas/estadisticas'),
  getListado: (params) => api.get('/descargas/listado', { params }),
  getPorTaller: (tallerId) => api.get(`/descargas/por-taller/${tallerId}`),
  getPorDocumento: (documentoId) => api.get(`/descargas/por-documento/${documentoId}`),
};

// Gestoría (formularios públicos + panel admin)
export const gestoriaAPI = {
  // Público
  buscarTalleres: (q) => api.get('/gestoria/talleres-search', { params: { q } }),
  crearSolicitud: (payload, files = []) => {
    const formData = new FormData();
    formData.append('payload', JSON.stringify(payload));
    (files || []).forEach((f) => formData.append('files', f));
    return api.post('/gestoria/solicitudes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  sigmaInteresado: (data) => api.post('/gestoria/sigma-interesados', data),
  getSeguimiento: (token) => api.get(`/gestoria/seguimiento/${token}`),
  listarComentariosPublico: (token) => api.get(`/gestoria/seguimiento/${token}/comentarios`),
  crearComentarioPublico: (token, texto, files = []) => {
    const fd = new FormData();
    fd.append('payload', JSON.stringify({ texto }));
    (files || []).forEach((f) => fd.append('files', f));
    return api.post(`/gestoria/seguimiento/${token}/comentarios`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  adjuntoPublicoUrl: (token, adjuntoId) => `${API}/gestoria/seguimiento/${token}/adjuntos/${adjuntoId}`,

  // Admin
  adminListar: (params) => api.get('/admin/gestoria/solicitudes', { params }),
  adminDetalle: (id) => api.get(`/admin/gestoria/solicitudes/${id}`),
  adminActualizar: (id, data) => api.patch(`/admin/gestoria/solicitudes/${id}`, data),
  adminStats: () => api.get('/admin/gestoria/stats'),
  adminListarSigma: () => api.get('/admin/gestoria/sigma-interesados'),
  adjuntoUrl: (solicitudId, adjuntoId) => {
    const t = localStorage.getItem('token') || '';
    return `${API}/admin/gestoria/solicitudes/${solicitudId}/adjuntos/${adjuntoId}?token=${encodeURIComponent(t)}`;
  },
  adminListarComentarios: (id) => api.get(`/admin/gestoria/solicitudes/${id}/comentarios`),
  adminCrearComentario: (id, texto, files = []) => {
    const fd = new FormData();
    fd.append('payload', JSON.stringify({ texto }));
    (files || []).forEach((f) => fd.append('files', f));
    return api.post(`/admin/gestoria/solicitudes/${id}/comentarios`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Pagos (MercadoPago)
export const pagosAPI = {
  // Configuración pública
  getConfig: () => api.get('/pagos/config'),
  
  // Crear preferencias de pago
  crearPreferenciaSuscripcion: (data) => api.post('/pagos/suscripcion/preferencia', data),
  crearPreferenciaUsuariosExtra: (data) => api.post('/pagos/usuarios-extra/preferencia', data),
  crearPreferenciaCurso: (data) => api.post('/pagos/curso/preferencia', data),
  crearPreferenciaDocumentos: (data) => api.post('/pagos/documentos/preferencia', data),
  crearPreferenciaCapacidades: (data) => api.post('/pagos/capacidades-temporales/preferencia', data),
  
  // Consultas
  getTransacciones: () => api.get('/pagos/transacciones'),
  getTransaccion: (externalReference) => api.get(`/pagos/transaccion/${externalReference}`),

  // Reconciliación con MercadoPago (super_admin)
  reconciliarBuscar: (dias = 30) => api.post(`/pagos/admin/reconciliar/buscar?dias=${dias}`),
  reconciliarAplicar: (paymentIds) => api.post('/pagos/admin/reconciliar/aplicar', { payment_ids: paymentIds }),
};

export const misDocumentosAPI = {
  // Registra apertura del documento (incrementa contador)
  registrarApertura: (manualId) => api.post(`/mis-documentos/apertura/${manualId}`),
  // Obtiene la última página leída
  getProgreso: (manualId) => api.get(`/mis-documentos/progreso/${manualId}`),
  // Guarda la página actual (al cerrar el visor)
  guardarProgreso: (manualId, pagina) => api.put(`/mis-documentos/progreso/${manualId}`, { pagina }),
  // Marca / desmarca como favorito
  setFavorito: (manualId, favorito) => api.put(`/mis-documentos/favorito/${manualId}`, { favorito }),
  // Resumen completo del usuario (favoritos + más usados + progreso por manual)
  getResumen: () => api.get('/mis-documentos/resumen'),
};

export const marcadoresAPI = {
  // Lista marcadores propios + compartidos del taller para un manual
  listar: (manualId) => api.get(`/marcadores/manual/${manualId}`),
  // Crea un marcador { manual_id, pagina, nota?, compartido }
  crear: (payload) => api.post('/marcadores', payload),
  // Actualiza nota o compartido (solo el autor)
  actualizar: (marcadorId, payload) => api.put(`/marcadores/${marcadorId}`, payload),
  // Elimina un marcador (solo el autor)
  eliminar: (marcadorId) => api.delete(`/marcadores/${marcadorId}`),
};

// Países (gestión dinámica de países, banderas, siglas y provincias)
export const paisesAPI = {
  // Público — solo activos (para dropdowns de formularios)
  getActivos: () => api.get('/paises/activos'),
  // Público — TODOS (activos + inactivos) para mostrar "Próximamente"
  getPublicos: () => api.get('/paises/publicos'),
  // Admin (super_admin) — incluye inactivos
  listar: () => api.get('/paises/'),
  crear: (data) => api.post('/paises/', data),
  actualizar: (paisId, data) => api.patch(`/paises/${paisId}`, data),
  eliminar: (paisId) => api.delete(`/paises/${paisId}`),
};

export default api;
